import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Input from "../../../../../../components/Input";
import { useCountryOptions } from "../../../../../../hooks/useCountryOptions";
import { useRelationOptions } from "../../../../../../hooks/useRelationOptions";
import { useBranchNames } from "../../../../../../hooks/useBranchNames";
import { useEffect, useState } from "react";
import theme from "../../../../../../theme";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUpload from "../../../../../../components/FileUpload";
import { getDocument } from "../../../../../../services/dashboard";

export default function ClaimantPersonalInfoForm({
  claimantPersonalData,
  iceClaimNumber,
  onClaimantCommentChange,
  documentData: initialDocumentData,
  isAssigned,
  claimStatus,
  claimantComment,
  commentData,
  isEditable,
  handleClaimantFieldChange,
}: any) {
  const { countryOptions, loading } = useCountryOptions();
  const { relationOptions } = useRelationOptions();
  const { branchOptions } = useBranchNames();

  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
   const [fileErrors, setFileErrors] = useState("");

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const addDocument = useClaimFormStore((state) => state.addDocument);
  const uploadedFile = useClaimFormStore(
    (state) => state.documents["claimant_id_document"]
  );
  const clearDocuments = useClaimFormStore((s) => s.clearDocuments);

  useEffect(() => {
    if (uploadedFile) {
      setDocuments([
        {
          file_name: uploadedFile.name,
          file_url: URL.createObjectURL(uploadedFile),
          isLocal: true,
        },
      ]);
    } else {
      setDocuments(initialDocumentData);
    }
  }, [uploadedFile]);

  const handleDeleteDocument = (index: number) => {
    const updatedDocs = [...documents];
    updatedDocs.splice(index, 1);
    setDocuments(updatedDocs);
  };

  const comments = commentData?.filter(
    (comment: any) => comment.section_type === "CLAIMANT_PERSONAL_INFORMATION"
  );

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const handlePreview = async (doc: any) => {
    try {
      if (doc.isLocal) {
        setSelectedDoc(doc);
        setOpenPreview(true);
      } else {
        const res = await getDocument(iceClaimNumber, doc.document_id);
        if (!res || res.status !== 200) {
          throw new Error("Failed to fetch document");
        }

        const blob = res.data;
        const url = URL.createObjectURL(blob);

        setSelectedDoc({ ...doc, file_url: url });
        setOpenPreview(true);
      }
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  useEffect(() => {
  if (!isEditable) {
    clearDocuments();
  }
}, [isEditable]);

  return (
    <Grid
      container
      rowSpacing={5}
      columnSpacing={10}
      mt={1}
      textAlign="left"
      p={3}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={claimantPersonalData?.title}
          height={40}
          width="100%"
          label="Title"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("title", e.target.value)
          }
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected: any) =>
              selected ? (
                selected
              ) : (
                <span style={{ color: "#aaa" }}>Select Title</span>
              ),
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  maxHeight: "300px",
                },
              },
            },
          }}
        >
          <MenuItem
            value="Adv"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            ADV
          </MenuItem>
          <MenuItem
            value="Dr"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            DR
          </MenuItem>
          <MenuItem
            value="Miss"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            MISS
          </MenuItem>
          <MenuItem
            value="Mr"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            MR
          </MenuItem>
          <MenuItem
            value="Mrs"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            MRS
          </MenuItem>
          <MenuItem
            value="Ms"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            MS
          </MenuItem>
          <MenuItem
            value="Prof"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            PROF
          </MenuItem>
          <MenuItem
            value="Rev"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            REV
          </MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.initials}
          height={40}
          width="100%"
          label="Initials"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("initials", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.first_name}
          height={40}
          width="100%"
          label="First name"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("first_name", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.last_name}
          height={40}
          width="100%"
          label="Last name"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("last_name", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={claimantPersonalData?.gender}
          height={40}
          width="100%"
          label="Gender"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("gender", e.target.value)
          }
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected: any) =>
              selected ? (
                selected
              ) : (
                <span style={{ color: "#aaa" }}>Select Gender</span>
              ),
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  maxHeight: "300px",
                },
              },
            },
          }}
        >
          <MenuItem
            value="Male"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            Male
          </MenuItem>
          <MenuItem
            value="female"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            Female
          </MenuItem>
          <MenuItem
            value="other"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            Other
          </MenuItem>
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          type="date"
          fullWidth
          label="Date of birth"
          variant="outlined"
          placeholder="DD-MM-YYYY"
          value={claimantPersonalData?.date_of_birth || ""}
          onChange={(e: any) =>
            handleClaimantFieldChange("date_of_birth", e.target.value)
          }
          disabled={!isEditable}
          height={40}
          width="100%"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={claimantPersonalData?.marital_status}
          height={40}
          width="100%"
          label="Marital status"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("marital_status", e.target.value)
          }
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected: any) =>
              selected ? (
                selected
              ) : (
                <span style={{ color: "#aaa" }}>Select Marital Status</span>
              ),
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  maxHeight: "300px",
                },
              },
            },
          }}
        >
          <MenuItem
            value="divorced"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            Divorced
          </MenuItem>
          <MenuItem
            value="married"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            Married
          </MenuItem>
          <MenuItem
            value="single"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            Single
          </MenuItem>
          <MenuItem
            value="widow"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            Widow
          </MenuItem>
          <MenuItem
            value="widower"
            sx={{
              background: "background.default",
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            Widower
          </MenuItem>
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.address}
          height={40}
          width="100%"
          label="Address"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("address", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={claimantPersonalData?.country_of_residence}
          height={40}
          width="100%"
          label="Country of Residence"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("country_of_residence", e.target.value)
          }
        >
          {(countryOptions.includes(claimantPersonalData?.country_of_residence)
            ? countryOptions
            : [...countryOptions, claimantPersonalData?.country_of_residence]
          ).map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </Input>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.contact_number}
          height={40}
          width="100%"
          label="Contact Number"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("contact_number", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={claimantPersonalData?.country_of_birth}
          height={40}
          width="100%"
          label="Country of Birth"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("country_of_birth", e.target.value)
          }
        >
          {(countryOptions.includes(claimantPersonalData?.country_of_birth)
            ? countryOptions
            : [...countryOptions, claimantPersonalData?.country_of_birth]
          ).map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={claimantPersonalData?.network_provider}
          height={40}
          width="100%"
          label="Network Provider"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("network_provider", e.target.value)
          }
        >
          <MenuItem
            value="Vodacom"
            sx={{
              background: "background.default",
              borderBottom: "1px solid #DFDDDD",
            }}
          >
            Vodacom
          </MenuItem>
          <MenuItem
            value="Telcom"
            sx={{
              background: "background.default",
              borderBottom: "1px solid #DFDDDD",
            }}
          >
            Telcom
          </MenuItem>
          <MenuItem
            value="Cell c"
            sx={{
              background: "background.default",
              borderBottom: "1px solid #DFDDDD",
            }}
          >
            Cell c
          </MenuItem>
          <MenuItem value="MTN" sx={{ background: "background.default" }}>
            MTN
          </MenuItem>
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.agent_or_broker}
          height={40}
          width="100%"
          label="Claim should take place with:"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("agent_or_broker", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.source_of_funds}
          height={40}
          width="100%"
          label="Source of Funds"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("source_of_funds", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          fullWidth
          label="Relationship"
          variant="outlined"
          value={claimantPersonalData.relationship}
          onChange={(e: any) =>
            handleClaimantFieldChange("relationship", e.target.value)
          }
          disabled={!isEditable}
          height={40}
          width="100%"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
        >
          {loading ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : (
            relationOptions.map((relation: string, index: number) => (
              <MenuItem
                key={index}
                value={relation}
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                {relation}
              </MenuItem>
            ))
          )}
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.identification_number}
          height={40}
          width="100%"
          label="Identification Number"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("identification_number", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.identification_type}
          height={40}
          width="100%"
          label="Identification Type"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("identification_type", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={claimantPersonalData?.branch_name}
          height={40}
          width="100%"
          label="Branch"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("branch_name", e.target.value)
          }
        >
          {(branchOptions.includes(claimantPersonalData?.branch_name)
            ? branchOptions
            : [...branchOptions, claimantPersonalData?.branch_name]
          ).map((branch) => (
            <MenuItem key={branch} value={branch}>
              {branch}
            </MenuItem>
          ))}
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          disabled={!isEditable}
          value={claimantPersonalData?.nationality || ""}
          height={40}
          width="100%"
          label="Nationality"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("nationality", e.target.value)
          }
        >
          {(countryOptions.includes(claimantPersonalData?.nationality)
            ? countryOptions
            : [...countryOptions, claimantPersonalData?.nationality]
          ).map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.email}
          height={40}
          width="100%"
          label="Email"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("email", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.address}
          height={40}
          width="100%"
          label="Physical/Postal address"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("address", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.postal_code}
          height={40}
          width="100%"
          label="Postal code"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("postal_code", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.beneficiary_employer}
          height={40}
          width="100%"
          label="Beneficiary's Employer"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("beneficiary_employer", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={claimantPersonalData?.occupation}
          height={40}
          width="100%"
          label="Occupation"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleClaimantFieldChange("occupation", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Box width="100%" height={48}>
          <Typography fontWeight={600} textAlign="left" fontSize={18}>
            Claimant's ID copy
          </Typography>
          {documents.map((doc: any, index: number) => (
            <Box
              key={index}
              sx={{
                background: "#F5EEE9",
                color: "#FF0000",
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                display: "flex",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                <PictureAsPdfIcon />
                <Box>
                  <Typography fontSize={14}>{doc.file_name}</Typography>
                  <Typography fontSize={12} color="text.secondary"></Typography>
                </Box>
              </Box>
              {isEditable ? (
                <IconButton
                  onClick={() => handleDeleteDocument(index)}
                  size="small"
                >
                  <DeleteIcon sx={{ color: "#FF0000" }} />
                </IconButton>
              ) : (
                <IconButton onClick={() => handlePreview(doc)} size="small">
                  <VisibilityIcon sx={{ color: "#EC6E00" }} />
                </IconButton>
              )}
            </Box>
          ))}
          {isEditable && documents.length === 0 && (
            <Box mt={2}>
              <FileUpload
                inputId="claimant-id"
                onChange={(e: any) => {
                  if (e.target.files.length > 0) {
                    const file = e.target.files[0];
                     if (file.size > 2 * 1024 * 1024) {
                    setFileErrors( "File must be less than 2MB")
                    return;
                  }
                    const newDoc = {
                      file_name: file.name,
                      file_url: URL.createObjectURL(file),
                      isLocal: true,
                    };
                    setDocuments([newDoc]);
                    addDocument("claimant_id_document", file);
                  }
                }}
              />
              {fileErrors && (
              <Typography color="error" fontSize={14}>
                {fileErrors}
              </Typography>
            )}
            </Box>
          )}
        </Box>
      </Grid>
      {comments?.length > 0 && (
        <Grid size={12} mt={4}>
          {comments.map((comment: any, index: number) => (
            <Accordion
              key={index}
              sx={{
                backgroundColor: "#F7F7F7",
                boxShadow: "none",
                borderRadius: "6px",
                mb: 2,
                border: "1px solid #E0E0E0",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography fontWeight={600} fontSize={14}>
                    {comment.commentedByName}{" "}
                    <Typography
                      component="span"
                      fontWeight={600}
                      color="#EC6E00"
                    >
                      - {comment.role_at_time_of_comment?.toUpperCase()}
                    </Typography>
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {new Date(comment.created_on).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}{" "}
                    | {formatDate(comment.created_on)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={15} color="#333" lineHeight={1.6}>
                  {comment.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      )}

      {isAssigned && claimStatus !== "PAID" && (
        <Grid size={{ md: 12 }} mt={4}>
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize={18}
            sx={{ display: "block", textAlign: "left" }}
          >
            Comment
          </Typography>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            disabled={isEditable}
            value={claimantComment}
            onChange={onClaimantCommentChange}
          />
        </Grid>
      )}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
